import { Trans } from "@lingui/react/macro"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { z } from "zod"
import { HowLink } from "@/features/list-of-materials/components/how-it-works/HowLink"
import { AllStoresDownload } from "@/features/list-of-materials/components/preview/AllStoresDownload"
import { StorePicker } from "@/features/list-of-materials/components/preview/StorePicker"
import { StorePreview } from "@/features/list-of-materials/components/preview/StorePreview"
import { Block, EmptyLine } from "@/features/list-of-materials/components/ui/Block"
import { PageHead } from "@/features/list-of-materials/components/ui/PageHead"
import { lomPreviewQueryOptions } from "@/features/list-of-materials/hooks/useLomData"
import { useSelectedLomVersion } from "@/features/list-of-materials/hooks/useLomVersion"

export const Route = createFileRoute("/(authenticated)/list-of-materials/preview")({
  validateSearch: z.object({
    store: z.string().optional(),
    /** A store number in the URL arrives as a number. */
    q: z.union([z.string(), z.number()]).transform(String).optional(),
    tab: z.enum(["meters", "totals", "plan"]).optional().catch(undefined),
    group: z.union([z.string(), z.number()]).transform(String).optional(),
    /** A meter within the group, "gondola-3": selected in the table and on the floor plan. */
    meter: z.string().optional(),
  }),
  component: PreviewPage,
})

function PreviewPage() {
  const { version, versionSearch } = useSelectedLomVersion()
  const { store, q, tab = "meters", group, meter } = Route.useSearch()
  const {
    data: preview,
    isPending,
    isError,
    error,
  } = useQuery(lomPreviewQueryOptions(version.id, store ?? ""))

  return (
    <>
      <PageHead
        help={<HowLink topic="preview" />}
        title={<Trans>Preview</Trans>}
        sub={
          <Trans>
            One store's List of Materials, meter by meter, computed from its approved blueprint.
          </Trans>
        }
      />
      {!store ? (
        <>
          <AllStoresDownload key={version.id} version={version} />
          <StorePicker version={versionSearch.version} initialQuery={q} />
        </>
      ) : isPending ? (
        <Block>
          <EmptyLine>
            <Trans>Computing the list of materials…</Trans>
          </EmptyLine>
        </Block>
      ) : isError ? (
        <Block>
          <EmptyLine>{error.message}</EmptyLine>
        </Block>
      ) : preview === null ? (
        <>
          <Block>
            <EmptyLine>
              <Trans>This store has no approved blueprint, or you have no access to it.</Trans>
            </EmptyLine>
          </Block>
          <div style={{ marginTop: "var(--sp-4)" }}>
            <StorePicker version={versionSearch.version} />
          </div>
        </>
      ) : (
        <StorePreview
          preview={preview}
          configVersionId={version.id}
          version={versionSearch.version}
          tab={tab}
          groupCode={group}
          meter={meter}
        />
      )}
    </>
  )
}
